import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormField } from "@tellescope/types-models";
import { areRequiredSubFieldsFilled, isBookingBlockedByEligibility, isValueEmpty } from "../../utils";
import type { NextFieldLogicOptions } from "../../utils/types";
import { TellescopeFieldResponse } from "../../types";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";

export interface IsFormCompleteArgs {
  visibleFields: (FormField & { answer: TellescopeFieldResponse["answer"] })[];
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">> | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
  fieldErrors: Record<string, string | null>;
  repeatMismatchFieldIds: ReadonlySet<string>;
  logicOptions: NextFieldLogicOptions;
}

/**
 * Whether every visible field that needs an answer has a non-empty one with no recorded error.
 *
 * "Has an error" means the entry is present and non-null — an unvalidated field is not treated as
 * valid here.
 */
export const isFormComplete = ({
  visibleFields,
  fieldsMap,
  currentResponses,
  fieldErrors,
  repeatMismatchFieldIds,
  logicOptions,
}: IsFormCompleteArgs): boolean => {
  const responsesArray = Object.values(currentResponses);

  return visibleFields.every((field) => {
    const fieldMetadata = fieldsMap?.[field.id];
    if (!fieldMetadata) return false;

    if (fieldMetadata.isOptional) return true;

    if (isBookingBlockedByEligibility(fieldMetadata, responsesArray)) return true;

    // A group's own error entry already carries the first error found across its sub-fields
    // (`useFieldValidation` lifts it there), so this one check covers them without re-deriving
    // an error rule per sub-field.
    if (fieldErrors[field.id] !== null && fieldErrors[field.id] !== undefined) return false;

    if (fieldMetadata.type === "Question Group") {
      return areRequiredSubFieldsFilled({
        group: fieldMetadata,
        fieldsMap: fieldsMap ?? {},
        responsesByFieldId: currentResponses,
        allResponses: responsesArray,
        repeatMismatchFieldIds,
        logicOptions,
      });
    }

    const response = currentResponses[field.id];
    if (!response) return false;

    // A consented signature with no name is not an empty value (`signed: true` makes the object
    // non-empty), so the emptiness check alone would let an unnamed signature complete the form.
    if (fieldMetadata.type === "signature") {
      const value = response.answer.value;
      if (typeof value !== "object" || value === null || !("fullName" in value)) return false;
      const typedName = typeof value.fullName === "string" ? value.fullName : "";
      return !!typedName.trim();
    }

    return !isValueEmpty(response.answer.value);
  });
};
